import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { KeycloakAuth } from "./keycloak";
import { resolveAuthConfig } from "./config";
import type { AuthState, UserInfo, Role } from "./types";

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  readonly children: ReactNode;
  /** Optional pre-configured KeycloakAuth instance (useful for testing). */
  readonly keycloakAuth?: KeycloakAuth;
}

/**
 * AuthProvider wraps the application and manages Keycloak OIDC authentication state.
 * Tokens are kept in memory only (not localStorage) for security.
 */
export function AuthProvider({
  children,
  keycloakAuth,
}: AuthProviderProps): React.JSX.Element {
  // The client is built after the issuer is resolved (the API server first,
  // then /config.js; see config.ts), so it stays null until then unless a
  // test injects one.
  const [auth, setAuth] = useState<KeycloakAuth | null>(keycloakAuth ?? null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function initAuth(): Promise<void> {
      try {
        let instance = keycloakAuth ?? null;
        if (instance === null) {
          const cfg = await resolveAuthConfig();
          // StrictMode runs this effect twice. The first run is cancelled
          // here, before it constructs a client, so only one client ever
          // calls init and the single-use authorization code is not raced.
          if (cancelled) return;
          instance = new KeycloakAuth(cfg.keycloakUrl, cfg.clientId);
          setAuth(instance);
        }
        const auth = instance;
        const authenticated = await auth.init();
        if (cancelled) return;

        if (authenticated) {
          setUser(auth.getUserInfo());
          setToken(auth.getToken());
          setIsAuthenticated(true);
        }
      } catch (err) {
        console.error("Keycloak initialization failed:", err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    // Fire-and-forget: initAuth() handles its own errors in the try/catch above.
    initAuth().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [keycloakAuth]);

  const refreshTokenCb = useCallback(async (): Promise<boolean> => {
    if (auth === null) return false;
    const ok = await auth.refreshToken();
    if (ok) {
      setToken(auth.getToken());
    }
    return ok;
  }, [auth]);

  const login = useCallback(() => {
    auth?.login();
  }, [auth]);

  const logout = useCallback(() => {
    auth?.logout();
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  }, [auth]);

  const role: Role | null = user?.role ?? null;

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      token,
      role,
      refreshToken: refreshTokenCb,
      login,
      logout,
    }),
    [user, isAuthenticated, isLoading, token, role, refreshTokenCb, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth state.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
